<template>
  <v-menu
    v-if="$vuetify.breakpoint.mdAndDown"
    v-model="showMenu"
    offset-y
    min-width="250"
  >
    <template #activator="{ on, attrs }">
      <NotificationBadge :notifications-length="total">
        <v-btn
          small
          icon
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </NotificationBadge>
    </template>
    <v-card>
      <v-card-title class="py-1 pr-1">
        <v-spacer />
        <v-btn icon class="cancel" @click="showMenu = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-list class="py-0">
        <NotificationMenu
          :notifications="notifications"
          :total="total"
          :has-more="hasMore"
          :loading="loading"
          @load:more="loadMore"
          @updateNotification="updateNotificationHandler"
          @clearAllNotifications="clearAllNotificationsHandler"
          @clearNotification="clearNotificationHandler"
        />
        <v-divider />
        <ProfileMenu />
        <v-divider />
        <v-list-item v-show="!isClient" :to="getAccountRoute({ name: 'administration-root-page'})">
          <v-list-item-icon class="ml-2 mr-3">
            <v-icon>mdi-cog</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="text--secondary">Account Settings</v-list-item-title>
        </v-list-item>
        <v-divider />
        <v-list-item>
          <v-spacer />
          <v-btn text class="cancel" @click="logoutHandler">
            <v-icon small class="mr-2">mdi-logout</v-icon> Logout
          </v-btn>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
  <div v-else class="d-flex align-center">
    <NotificationMenu
      :notifications="notifications"
      :total="total"
      :has-more="hasMore"
      :loading="loading"
      @load:more="loadMore"
      @updateNotification="updateNotificationHandler"
      @clearAllNotifications="clearAllNotificationsHandler"
      @clearNotification="clearNotificationHandler"
    />
    <ProfileMenu />
  </div>
</template>

<script lang="ts">
import { mapActions } from 'vuex';
import mixins from 'vue-typed-mixins';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { RequestAbortMixin } from '@/bundles/App/mixins/RequestAbortMixin';
import { notificationService } from '@/bundles/Notification/factories/notificationServiceFactory';
import { INotification } from '@/bundles/Notification/interfaces/INotification';
import { REQUEST_CANCELLED_STATUS } from '@/bundles/Http/mappers/ErrorMapper';
import { RootActions } from '@/store/types/action-types';
import { SocketEventTypesEnum } from '@/helpers/sockets/event-types';
import { socketInstance } from '@/plugins/socket/socket';

import NotificationMenu from '@/bundles/Panels/components/NotificationMenu.vue';
import NotificationBadge from '@/bundles/Panels/components/NotificationBadge.vue';
import ProfileMenu from '@/bundles/Panels/components/ProfileMenu.vue';

const NOTIFICATIONS_PAGE_SIZE = 20;

interface IProfileMenuWrapper {
  showMenu: boolean;
  notifications: INotification[];
  total: number;
  loading: boolean;
}

export default mixins(AccountLayoutMixin, RequestAbortMixin).extend({
  name: 'ProfileMenuWrapper',

  components: { ProfileMenu, NotificationMenu, NotificationBadge },

  data: (): IProfileMenuWrapper => ({
    showMenu: false,
    notifications: [],
    total: 0,
    loading: false,
  }),

  computed: {
    hasMore (): boolean {
      return this.notifications.length < this.total;
    },
  },

  created () {
    this.resetNotifications();
    socketInstance?.on(SocketEventTypesEnum.REFRESH_NOTIFICATIONS, this.resetNotifications);
  },

  beforeDestroy () {
    socketInstance?.off(SocketEventTypesEnum.REFRESH_NOTIFICATIONS, this.resetNotifications);
    this.abortController?.abort();
  },

  methods: {
    ...mapActions({
      logout: RootActions.LOG_OUT
    }),
    resetNotifications () {
      this.notifications = [];
      this.total = 0;
      this.loadMore();
    },
    async loadMore () {
      if (this.loading) return;

      if (this.notifications.length > 0 && !this.hasMore) return;

      this.handleRequestAbort();
      this.loading = true;

      try {
        const { data, total } = await notificationService.getMyNotifications({
          take: NOTIFICATIONS_PAGE_SIZE,
          skip: this.notifications.length,
          controller: this.abortController,
        });

        this.notifications = [...this.notifications, ...data];
        this.total = total;
      } catch (error: any) {
        this.addNotification({ ...error, status: error?.status === REQUEST_CANCELLED_STATUS ? REQUEST_CANCELLED_STATUS : 0 });
      } finally {
        this.loading = false;
      }
    },
    logoutHandler: async function () {
      try {
        await this.logout();
      } catch (error: any) {
        this.addNotification({ ...error });
      }
    },
    updateNotificationHandler (notification: INotification) {
      this.notifications = this.notifications.map(tempNotification => {
        if (tempNotification.id === notification.id) {
          return notification;
        }

        return tempNotification;
      });
    },
    clearNotificationHandler (id: number) {
      this.notifications = this.notifications.filter((item) => item.id !== id);
      this.total = Math.max(0, this.total - 1);
    },
    clearAllNotificationsHandler () {
      this.notifications = [];
      this.total = 0;
    }
  }
})
</script>
